// Scroll utility functions for robust element targeting and behavior coordination

export interface ScrollOptions {
  behavior?: 'smooth' | 'instant';
  block?: 'start' | 'center' | 'end' | 'nearest';
  inline?: 'start' | 'center' | 'end' | 'nearest';
  retries?: number;
  retryDelay?: number;
  offset?: number;
}

/**
 * Wait for an element to exist in the DOM with timeout and retry logic
 */
export const waitForElement = (
  selector: string, 
  timeout = 5000, 
  checkInterval = 100
): Promise<Element | null> => {
  return new Promise((resolve) => {
    const startTime = Date.now();
    
    const checkElement = () => {
      const element = document.getElementById(selector.replace('#', '')) || 
                     document.querySelector(selector);
      
      if (element) {
        resolve(element);
        return;
      }
      
      if (Date.now() - startTime >= timeout) {
        resolve(null);
        return;
      }
      
      setTimeout(checkElement, checkInterval);
    };
    
    checkElement();
  });
};

/**
 * Scroll to element with retry logic and proper coordination
 */
export const scrollToElementWithRetry = async (
  elementId: string,
  options: ScrollOptions = {}
): Promise<boolean> => {
  const {
    behavior = 'smooth',
    block = 'start',
    retries = 3,
    retryDelay = 100,
    offset = 0
  } = options;

  for (let attempt = 0; attempt < retries; attempt++) {
    // Wait for element to exist
    const element = await waitForElement(elementId, 2000);
    
    if (element) {
      // Additional wait to ensure element is fully rendered and positioned
      await new Promise(resolve => setTimeout(resolve, 50));
      
      try {
        // Use scrollIntoView with proper options
        element.scrollIntoView({
          behavior,
          block,
          inline: 'nearest'
        });
        
        // Apply offset if specified
        if (offset !== 0) {
          await new Promise(resolve => setTimeout(resolve, 100));
          window.scrollBy(0, offset);
        }
        
        return true;
      } catch (error) {
        console.warn(`Scroll attempt ${attempt + 1} failed:`, error);
      }
    }
    
    // Wait before retry
    if (attempt < retries - 1) {
      await new Promise(resolve => setTimeout(resolve, retryDelay));
    }
  }
  
  return false;
};

/**
 * Coordinate scroll behavior and prevent conflicts
 */
export class ScrollCoordinator {
  private static instance: ScrollCoordinator;
  private isScrollLocked = false;
  private pendingScrolls = new Set<string>();
  
  static getInstance(): ScrollCoordinator {
    if (!ScrollCoordinator.instance) {
      ScrollCoordinator.instance = new ScrollCoordinator();
    }
    return ScrollCoordinator.instance;
  }
  
  async executeScroll(
    elementId: string, 
    options: ScrollOptions = {},
    lockDuration = 1000
  ): Promise<boolean> {
    // Prevent concurrent scrolls to the same element
    if (this.pendingScrolls.has(elementId)) {
      return false;
    }
    
    this.pendingScrolls.add(elementId);
    this.isScrollLocked = true;
    
    try {
      const success = await scrollToElementWithRetry(elementId, options);
      
      // Keep scroll locked for specified duration to prevent interference
      setTimeout(() => {
        this.isScrollLocked = false;
      }, lockDuration);
      
      return success;
    } finally {
      this.pendingScrolls.delete(elementId);
    }
  }
  
  isLocked(): boolean {
    return this.isScrollLocked;
  }
  
  unlock(): void {
    this.isScrollLocked = false;
    this.pendingScrolls.clear();
  }
}

/**
 * Disable browser scroll restoration temporarily
 */
export const withScrollRestorationDisabled = async <T>(
  callback: () => Promise<T>
): Promise<T> => {
  const originalScrollRestoration = window.history.scrollRestoration;
  
  try {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    return await callback();
  } finally {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = originalScrollRestoration;
    }
  }
};
